// CREW-1079 — a seafarer's age and birthday, from the date of birth as the
// backend prints it: "dd.mm.yyyy" (CrewingApplicant#birthday).
//
// Calendar arithmetic on the three numbers only, never `Date` subtraction: a
// date that crosses a time zone is how CREW-1162 moved birthdays by a day.
// `today` is the viewer's own calendar day.

const PATTERN = /^(\d{2})\.(\d{2})\.(\d{4})$/;

// Ages outside this range come from a mistyped date, not a seafarer —
// 1,029 of 84,500 profiles on a production copy (September 2026).
const MIN_AGE = 16;
const MAX_AGE = 80;

const isLeap = (year) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const parse = (value) => {
  const match = PATTERN.exec(value || '');
  if (!match) return null;

  const [day, month, year] = match.slice(1).map(Number);
  const probe = new Date(year, month - 1, day);
  const real = probe.getFullYear() === year && probe.getMonth() === month - 1 && probe.getDate() === day;
  return real ? { day, month, year } : null;
};

// Someone born on 29 February celebrates on the 28th in a common year.
const birthdayIn = (year, { day, month }) => (
  month === 2 && day === 29 && !isLeap(year) ? { month: 2, day: 28 } : { month, day }
);

/**
 * @param {string} value "dd.mm.yyyy"
 * @param {Date} [today]
 * @returns {{date: string, age: number, plausible: boolean, today: boolean} | null}
 *   null when there is no readable date
 */
export const describeBirthday = (value, today = new Date()) => {
  const born = parse(value);
  if (!born) return null;

  const year = today.getFullYear();
  const month = today.getMonth() + 1;
  const day = today.getDate();
  const birthday = birthdayIn(year, born);
  const hadBirthday = month > birthday.month || (month === birthday.month && day >= birthday.day);
  const age = year - born.year - (hadBirthday ? 0 : 1);
  const plausible = age >= MIN_AGE && age <= MAX_AGE;

  return {
    date: value,
    age,
    plausible,
    today: plausible && month === birthday.month && day === birthday.day,
  };
};
